// @flow
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { WeekOptions } from '@justpark/ui/src/components/LongTermDurationPicker';
import ChevronIcon from '@justpark/ui/src/components/Icons/ChevronIcon';
import useNativeInputs from '@justpark/ui/src/hooks/useNativeInputs';
import LongTermDurationPicker from '../../LazyLongTermDurationPicker';
import NativeDatePicker from '../DatePicker/NativeDatePicker';
import useWindowSize from '../../../hooks/useWindowSize';
import getDateFromNow from '../../../helpers/dates/getDateFromNow';

import '../../../../static/scss/components/bookingDate.scss';

type Props = {
  dateFrom: string,
  dateTo: string,
  dateFromRaw: string,
  dateToRaw: string,
  modifier: string,
  datePickerTarget: boolean,
  datePickerVisible: boolean,
  isValid: boolean,
  onClickHandler: () => void,
  onNativeDateChange: () => void,
  isMonthly: boolean,
  onMonthlyTypeChange: () => {},
  monthlyType: WeekOptions,
  showLessThanOneMonth?: boolean
};

// if no date provided, the element will be "disabled" ( different styling )
const BookingDate = ({
  dateFrom,
  dateTo,
  dateFromRaw,
  dateToRaw,
  modifier = 'single',
  datePickerTarget = false,
  datePickerVisible = false,
  isValid = true,
  onClickHandler = () => {},
  onNativeDateChange = () => {},
  isMonthly,
  monthlyType,
  onMonthlyTypeChange,
  showLessThanOneMonth = false
}: Props) => {
  const { t } = useTranslation();

  const useNative = useNativeInputs();

  const placeholder = isMonthly
    ? t('search:bookingDatePlaceholderMonthly', 'Select a date')
    : t('search:bookingDatePlaceholderDaily', 'Select a date & time');
  const innerDateFrom = {
    date: dateFrom || placeholder,
    modifier: dateFrom ? 'enabled' : 'disabled'
  };

  const innerDateTo = {
    date: dateTo || placeholder,
    modifier: dateTo ? 'enabled' : 'disabled'
  };

  const baseClass = 'c-booking-date__date';
  const validClass = `${baseClass}--${isValid ? 'valid' : 'invalid'}`;

  const { width } = useWindowSize();
  const isMobile = width < 640;

  return (
    <div
      className={`c-booking-date c-booking-date--${modifier} ${
        isMonthly ? 'c-booking-date--is-monthly' : ''
      }`}
    >
      <NativeDatePicker
        value={dateFromRaw}
        min={getDateFromNow(-1, 'day')}
        max={getDateFromNow(1, 'year')}
        className="c-booking-date__native-input c-booking-date__native-input--from"
        output={false}
        name="from"
        onChange={(timestamp) => {
          onNativeDateChange('arriving', timestamp);
        }}
        onClick={() => {
          const shouldOpenNonNativePicker = false;
          onClickHandler('from', shouldOpenNonNativePicker);
        }}
        shouldRender={useNative}
        showTimePicker={!isMonthly}
      >
        <div
          onClick={onClickHandler.bind(this, 'from')}
          className={`${baseClass} ${baseClass}--${modifier} ${
            datePickerVisible && datePickerTarget === 'from' ? 'is-active' : ''
          } ${baseClass}--${innerDateTo.modifier} ${validClass}`}
        >
          {modifier === 'single' && (
            <h6>
              <span className="c-booking-date__native-input__span">
                {isMonthly
                  ? t('search:bookingDateStartLabelMonthly', 'Starting on')
                  : t('search:bookingDateStartLabelDaily', 'Parking from')}
              </span>
            </h6>
          )}
          <span
            className="c-booking-date__date-display"
            data-cy="booking-start-date"
          >
            {innerDateTo.date}
          </span>
          <ChevronIcon className="c-booking-date__native-input__chevron" />
        </div>
      </NativeDatePicker>
      {isMonthly && (
        <LongTermDurationPicker
          selected={monthlyType}
          onOptionSelect={onMonthlyTypeChange}
          includesLessThanOneMonth={showLessThanOneMonth}
          label={t('dates:longTermBookingDurationLabel', 'Duration')}
          labelColor="muted"
          labelSize="subtext"
          t={t}
          className="c-booking-date__loading-text"
          isMobile={isMobile}
        />
      )}
      {!isMonthly && (
        <NativeDatePicker
          value={dateToRaw}
          min={dateFromRaw}
          max={getDateFromNow(1, 'year')}
          className="c-booking-date__native-input c-booking-date__native-input--to"
          output={false}
          name="to"
          onChange={(timestamp) => {
            onNativeDateChange('leaving', timestamp);
          }}
          onClick={() => {
            const shouldOpenNonNativePicker = false;
            onClickHandler('to', shouldOpenNonNativePicker);
          }}
          shouldRender={useNative}
          showTimePicker
        >
          <div
            onClick={onClickHandler.bind(this, 'to')}
            className={`${baseClass} ${baseClass}--${modifier} ${
              datePickerVisible && datePickerTarget === 'to' ? 'is-active' : ''
            } ${baseClass}--${innerDateFrom.modifier} ${validClass}`}
          >
            {modifier === 'single' && (
              <h6>
                <span className="c-booking-date__native-input__span">
                  {t('search:bookingDateEndLabel', 'Parking until')}
                </span>
              </h6>
            )}
            <span className="c-booking-date__date-display">
              {innerDateFrom.date}
            </span>
            <ChevronIcon className="c-booking-date__native-input__chevron" />
          </div>
        </NativeDatePicker>
      )}
    </div>
  );
};

export default BookingDate;
